import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'http://localhost:8000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' } };
};

const FIELD_OPTIONS = [
    { value: 'status', label: 'Status' },
    { value: 'approved_headcount', label: 'Approved Headcount' },
    { value: 'unit_type_id', label: 'Unit Type' },
    { value: 'manager_position_id', label: 'Manager Position' },
    { value: 'hr_profile_id', label: 'HR Settings Profile' },
    { value: 'is_manager_position', label: 'Is Manager Position (positions only)' },
];

const ScheduledChangesList = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('selectedCompanyId') || 1;

    const [changes, setChanges] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(null);

    const emptyForm = {
        changeable_type: 'department', changeable_id: '', field: 'status',
        value: '', effective_date: '', reason: '',
    };
    const [formData, setFormData] = useState({ ...emptyForm });

    const handleError = (err) => {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); return true;
        }
        return false;
    };

    const fetchAll = async () => {
        try {
            setLoading(true);
            const [changesRes, deptRes, posRes] = await Promise.all([
                axios.get(`${API_BASE}/companies/${companyId}/scheduled-changes`, getAuthHeaders()),
                axios.get(`${API_BASE}/companies/${companyId}/hr-departments`, getAuthHeaders()),
                axios.get(`${API_BASE}/companies/${companyId}/hr-positions`, getAuthHeaders()),
            ]);
            setChanges(changesRes.data.data || changesRes.data || []);
            setDepartments(deptRes.data.data || deptRes.data || []);
            setPositions(posRes.data.data || posRes.data || []);
            setError(null);
        } catch (err) {
            if (!handleError(err)) setError('Failed to fetch scheduled changes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [companyId]);

    const openCreate = () => {
        setFormData({ ...emptyForm });
        setShowModal(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/scheduled-changes`, {
                changeable_type: formData.changeable_type,
                changeable_id: formData.changeable_id,
                changes: { [formData.field]: formData.value },
                effective_date: formData.effective_date,
                reason: formData.reason || null,
            }, getAuthHeaders());
            setShowModal(false);
            fetchAll();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error scheduling change.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = async (change) => {
        try {
            await axios.delete(`${API_BASE}/companies/${companyId}/scheduled-changes/${change.id}`, getAuthHeaders());
            setShowCancelConfirm(null);
            fetchAll();
        } catch (err) {
            if (!handleError(err)) alert(err.response?.data?.message || 'Error cancelling change.');
        }
    };

    const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const targetOptions = formData.changeable_type === 'department' ? departments : positions;

    // Filters
    const filtered = changes.filter(c => {
        if (statusFilter !== 'All' && c.status !== statusFilter) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (c.changeable_name || '').toLowerCase().includes(s) || (c.reason || '').toLowerCase().includes(s);
        }
        return true;
    });

    // Summary
    const total = changes.length;
    const pending = changes.filter(c => c.status === 'Pending').length;
    const applied = changes.filter(c => c.status === 'Applied').length;
    const cancelled = changes.filter(c => c.status === 'Cancelled').length;

    // ── Styles ──
    const pageStyle = { padding: '24px 32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
    const titleStyle = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: 0 };
    const btnPrimary = { padding: '10px 20px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' };
    const btnDanger = { padding: '6px 14px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' };

    const cardsRow = { display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' };
    const card = (color) => ({ flex: '1', minWidth: '140px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` });
    const cardLabel = { fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' };
    const cardValue = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a' };

    const filterRow = { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' };
    const pill = (isActive) => ({ padding: '6px 16px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', backgroundColor: isActive ? '#2563eb' : '#e2e8f0', color: isActive ? '#fff' : '#475569' });
    const searchBox = { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', width: '280px', outline: 'none' };
    const tableWrap = { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' };
    const th = { padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' };
    const td = { padding: '12px 16px', fontSize: '0.875rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };

    const statusBadge = (status) => {
        const map = { Pending: { bg: '#fef3c7', color: '#92400e' }, Applied: { bg: '#dcfce7', color: '#166534' }, Cancelled: { bg: '#f1f5f9', color: '#64748b' } };
        const s = map[status] || map.Pending;
        return { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: s.bg, color: s.color };
    };

    const typeBadge = (type) => ({
        display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '600',
        backgroundColor: type === 'department' ? '#dbeafe' : '#f3e8ff',
        color: type === 'department' ? '#1d4ed8' : '#7c3aed',
    });

    const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 };
    const modalBox = { backgroundColor: '#fff', borderRadius: '12px', width: '560px', maxHeight: '85vh', overflowY: 'auto', padding: '0' };
    const modalHeader = { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
    const modalBody = { padding: '24px' };
    const modalFooter = { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' };
    const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
    const selectStyle = { ...inputStyle, backgroundColor: '#fff' };
    const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#374151', marginBottom: '4px' };
    const fieldGroup = { marginBottom: '16px' };
    const gridTwo = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };

    if (initialLoading) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#64748b', marginTop: '60px' }}>Loading scheduled changes...</p></div>;
    if (error) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#dc2626', marginTop: '60px' }}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div>
                    <h1 style={titleStyle}>Scheduled Changes</h1>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '4px 0 0 0' }}>
                        Future-dated changes auto-apply on their effective date.
                    </p>
                </div>
                <button style={btnPrimary} onClick={openCreate}>+ Schedule Change</button>
            </div>

            {/* Summary Cards */}
            <div style={cardsRow}>
                <div style={card('#2563eb')}><div style={cardLabel}>Total</div><div style={cardValue}>{total}</div></div>
                <div style={card('#d97706')}><div style={cardLabel}>Pending</div><div style={cardValue}>{pending}</div></div>
                <div style={card('#16a34a')}><div style={cardLabel}>Applied</div><div style={cardValue}>{applied}</div></div>
                <div style={card('#64748b')}><div style={cardLabel}>Cancelled</div><div style={cardValue}>{cancelled}</div></div>
            </div>

            {/* Filters */}
            <div style={filterRow}>
                <button style={pill(statusFilter === 'All')} onClick={() => setStatusFilter('All')}>All</button>
                <button style={pill(statusFilter === 'Pending')} onClick={() => setStatusFilter('Pending')}>Pending</button>
                <button style={pill(statusFilter === 'Applied')} onClick={() => setStatusFilter('Applied')}>Applied</button>
                <button style={pill(statusFilter === 'Cancelled')} onClick={() => setStatusFilter('Cancelled')}>Cancelled</button>
                <div style={{ flex: 1 }} />
                <input style={searchBox} placeholder="Search by target name, reason..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {/* Table */}
            <div style={tableWrap}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>Target</th>
                                <th style={th}>Type</th>
                                <th style={th}>Change</th>
                                <th style={th}>Effective Date</th>
                                <th style={th}>Reason</th>
                                <th style={th}>Status</th>
                                <th style={th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No scheduled changes found.</td></tr>
                            ) : filtered.map(c => (
                                <tr key={c.id} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                                    <td style={{ ...td, fontWeight: '600', color: '#0f172a' }}>{c.changeable_name || `ID: ${c.changeable_id}`}</td>
                                    <td style={td}><span style={typeBadge(c.changeable_type)}>{c.changeable_type}</span></td>
                                    <td style={td}>
                                        {c.changes && Object.entries(c.changes).map(([k, v]) => (
                                            <div key={k} style={{ fontSize: '0.8rem' }}>
                                                <span style={{ color: '#64748b' }}>{k}:</span>{' '}
                                                <span style={{ fontWeight: '600' }}>{String(v)}</span>
                                            </div>
                                        ))}
                                    </td>
                                    <td style={td}>
                                        <span style={{ fontWeight: '500' }}>{c.effective_date}</span>
                                    </td>
                                    <td style={td}>
                                        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{c.reason || '—'}</span>
                                    </td>
                                    <td style={td}><span style={statusBadge(c.status)}>{c.status}</span></td>
                                    <td style={td}>
                                        {c.status === 'Pending' && (
                                            <button style={btnDanger} onClick={() => setShowCancelConfirm(c)}>Cancel</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create Modal */}
            {showModal && (
                <div style={overlay} onClick={() => setShowModal(false)}>
                    <div style={modalBox} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>Schedule a Change</h2>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                        </div>
                        <div style={modalBody}>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Target Type *</label>
                                    <select style={selectStyle} value={formData.changeable_type} onChange={e => { updateForm('changeable_type', e.target.value); updateForm('changeable_id', ''); }}>
                                        <option value="department">Department</option>
                                        <option value="position">Position</option>
                                    </select>
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Target *</label>
                                    <select style={selectStyle} value={formData.changeable_id} onChange={e => updateForm('changeable_id', e.target.value)}>
                                        <option value="">Select...</option>
                                        {targetOptions.map(t => (
                                            <option key={t.id} value={t.id}>{t.name || t.title} ({t.code})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Field to Change *</label>
                                    <select style={selectStyle} value={formData.field} onChange={e => updateForm('field', e.target.value)}>
                                        {FIELD_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>New Value *</label>
                                    <input style={inputStyle} value={formData.value} onChange={e => updateForm('value', e.target.value)} placeholder={formData.field === 'status' ? 'e.g. Inactive' : 'New value'} />
                                </div>
                            </div>
                            <div style={gridTwo}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Effective Date *</label>
                                    <input style={inputStyle} type="date" value={formData.effective_date} onChange={e => updateForm('effective_date', e.target.value)} />
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>Reason (optional)</label>
                                    <input style={inputStyle} value={formData.reason} onChange={e => updateForm('reason', e.target.value)} placeholder="Why this change?" />
                                </div>
                            </div>
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowModal(false)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Cancel</button>
                            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'Scheduling...' : 'Schedule Change'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Confirm */}
            {showCancelConfirm && (
                <div style={overlay} onClick={() => setShowCancelConfirm(null)}>
                    <div style={{ ...modalBox, width: '420px' }} onClick={e => e.stopPropagation()}>
                        <div style={modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#dc2626' }}>Cancel Scheduled Change</h2></div>
                        <div style={modalBody}>
                            <p style={{ color: '#475569', margin: 0 }}>
                                Cancel the scheduled change for <strong>"{showCancelConfirm.changeable_name}"</strong> on {showCancelConfirm.effective_date}?
                            </p>
                        </div>
                        <div style={modalFooter}>
                            <button onClick={() => setShowCancelConfirm(null)} style={{ ...btnPrimary, backgroundColor: '#e2e8f0', color: '#475569' }}>Keep</button>
                            <button onClick={() => handleCancel(showCancelConfirm)} style={{ ...btnPrimary, backgroundColor: '#dc2626' }}>Cancel Change</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduledChangesList;